import { useCallback } from 'react';
import { Flex, Chip, Column } from '@elementary/common-ui/components';
import { Tooltip } from '@mui/material';
import { FieldChange, Period } from '@elementary/common-ui/types';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import {
  CellFlex,
  ElasticChipWrapper,
  FixedChipWrapper,
  ShrinkableChipWrapper,
} from './styles';
import { BasePreviewRow } from './types';
import {
  formatConfigChangeValue,
  formatParamValue,
  formatThreshold,
  isValidPeriod,
} from './utils';

type ListColumnOptions = {
  isElasticLayout?: boolean;
};

const useConfigPreviewColumnBuilders = <
  TRow extends BasePreviewRow<unknown>,
>() => {
  const buildPeriodColumn = useCallback(
    (
      id: string,
      header: string,
      getField: (row: TRow) => FieldChange<Period | null> | undefined,
    ): Column<TRow> => ({
      id,
      header,
      showValueWhenEmpty: true,
      filterable: false,
      nonToggleable: true,
      renderer: (row: TRow) => {
        const field = getField(row);
        if (!field) return <></>;
        const currentLabel = formatThreshold(field.current);
        const newPeriod = isValidPeriod(field.new) ? field.new : null;
        const newLabel = formatThreshold(newPeriod);
        const isChanged = currentLabel !== newLabel;
        const showCurrent = isChanged && !row.isCreateRow;

        return (
          <Flex sx={{ gap: 1, alignItems: 'center' }}>
            {showCurrent && (
              <Chip label={currentLabel} customVariant="crossed" />
            )}
            <Chip label={newLabel} customVariant="dashed" />
          </Flex>
        );
      },
    }),
    [],
  );

  const buildDropdownColumn = useCallback(
    (
      id: string,
      header: string,
      options: { value: string; label: string }[],
      getField: (row: TRow) => FieldChange<string | null> | undefined,
    ): Column<TRow> => ({
      id,
      header,
      showValueWhenEmpty: true,
      filterable: false,
      nonToggleable: true,
      renderer: (row: TRow) => {
        const field = getField(row);
        if (!field) return <></>;
        const currentLabel =
          options.find((o) => o.value === field.current)?.label ??
          field.current ??
          'None';
        const newLabel =
          options.find((o) => o.value === field.new)?.label ??
          field.new ??
          'None';
        const isChanged = field.current !== field.new;
        const showCurrent = isChanged && !row.isCreateRow;

        return (
          <Flex sx={{ gap: 1, alignItems: 'center' }}>
            {showCurrent && (
              <Chip label={currentLabel} customVariant="crossed" />
            )}
            <Chip label={newLabel} customVariant="dashed" />
          </Flex>
        );
      },
    }),
    [],
  );

  const buildTextColumn = useCallback(
    (
      id: string,
      header: string,
      getField: (row: TRow) => FieldChange<string | null> | undefined,
    ): Column<TRow> => ({
      id,
      header,
      showValueWhenEmpty: true,
      filterable: false,
      nonToggleable: true,
      width: 220,
      renderer: (row: TRow) => {
        const field = getField(row);
        if (!field) return <></>;
        const currentLabel = field.current?.trim() ? field.current : 'None';
        const newLabel = field.new?.trim() ? field.new : 'None';
        const isChanged = field.current !== field.new;
        const showCurrent = isChanged && !row.isCreateRow;

        return (
          <CellFlex>
            {showCurrent && (
              <ShrinkableChipWrapper>
                <Tooltip title={currentLabel}>
                  <span>
                    <Chip label={currentLabel} customVariant="crossed" />
                  </span>
                </Tooltip>
              </ShrinkableChipWrapper>
            )}
            <ShrinkableChipWrapper>
              <Tooltip title={newLabel}>
                <span>
                  <Chip label={newLabel} customVariant="dashed" />
                </span>
              </Tooltip>
            </ShrinkableChipWrapper>
          </CellFlex>
        );
      },
    }),
    [],
  );

  const buildNumberColumn = useCallback(
    (
      id: string,
      header: string,
      getField: (row: TRow) => FieldChange<number | null> | undefined,
      suffix = '',
    ): Column<TRow> => ({
      id,
      header,
      showValueWhenEmpty: true,
      filterable: false,
      nonToggleable: true,
      renderer: (row: TRow) => {
        const field = getField(row);
        if (!field) return <></>;
        const format = (value: number | null) =>
          value == null ? 'None' : `${value}${suffix}`;
        const currentLabel = format(field.current);
        const newLabel = format(field.new);
        const isChanged = field.current !== field.new;
        const showCurrent = isChanged && !row.isCreateRow;

        return (
          <Flex sx={{ gap: 1, alignItems: 'center' }}>
            {showCurrent && (
              <Chip label={currentLabel} customVariant="crossed" />
            )}
            <Chip label={newLabel} customVariant="dashed" />
          </Flex>
        );
      },
    }),
    [],
  );

  const buildBooleanColumn = useCallback(
    (
      id: string,
      header: string,
      trueLabel: string,
      falseLabel: string,
      getField: (row: TRow) => FieldChange<boolean> | undefined,
    ): Column<TRow> => ({
      id,
      header,
      showValueWhenEmpty: true,
      filterable: false,
      nonToggleable: true,
      renderer: (row: TRow) => {
        const field = getField(row);
        if (!field) return <></>;
        const currentLabel = field.current ? trueLabel : falseLabel;
        const newLabel = field.new ? trueLabel : falseLabel;
        const isChanged = field.current !== field.new;
        const showCurrent = isChanged && !row.isCreateRow;

        return (
          <Flex sx={{ gap: 1, alignItems: 'center' }}>
            {showCurrent && (
              <Chip label={currentLabel} customVariant="crossed" />
            )}
            <Chip label={newLabel} customVariant="dashed" />
          </Flex>
        );
      },
    }),
    [],
  );

  const buildListColumn = useCallback(
    (
      id: string,
      header: string,
      itemLabel: [string, string],
      getField: (row: TRow) => FieldChange<string[]> | undefined,
      { isElasticLayout = false }: ListColumnOptions = {},
    ): Column<TRow> => ({
      id,
      header,
      showValueWhenEmpty: true,
      filterable: false,
      nonToggleable: true,
      width: 200,
      renderer: (row: TRow) => {
        const field = getField(row);
        if (!field) return <></>;

        const currentValues = field.current ?? [];
        const newValues = field.new ?? [];

        const formatList = (items: string[]) =>
          items.length <= 1
            ? items[0] || 'None'
            : pluralizeString(items.length, ...itemLabel);

        const currentDisplay = formatList(currentValues);
        const newDisplay = formatList(newValues);
        const currentFull = currentValues.join(', ');
        const newFull = newValues.join(', ');
        const isChanged =
          currentValues.slice().sort().join(',') !==
          newValues.slice().sort().join(',');

        const showCurrent = isChanged && !row.isCreateRow;

        const currentChip = isElasticLayout ? (
          <FixedChipWrapper>
            <Tooltip title={currentFull} disableHoverListener={!isChanged}>
              <span>
                <Chip label={currentDisplay} customVariant="crossed" />
              </span>
            </Tooltip>
          </FixedChipWrapper>
        ) : (
          <Chip label={currentDisplay} customVariant="crossed" />
        );

        const newChip = (
          <Tooltip
            title={newFull}
            disableHoverListener={newValues.length === 0}
          >
            <span>
              <Chip label={newDisplay} customVariant="dashed" />
            </span>
          </Tooltip>
        );

        return (
          <CellFlex>
            {showCurrent && currentChip}
            {isElasticLayout ? (
              <ElasticChipWrapper>{newChip}</ElasticChipWrapper>
            ) : (
              newChip
            )}
          </CellFlex>
        );
      },
    }),
    [],
  );

  const buildParamsColumn = useCallback(
    (
      id: string,
      header: string,
      getParams: (
        row: TRow,
      ) => Record<string, FieldChange<unknown>> | undefined,
    ): Column<TRow> => ({
      id,
      header,
      showValueWhenEmpty: true,
      filterable: false,
      nonToggleable: true,
      width: 240,
      renderer: (row: TRow) => {
        const params = getParams(row);
        if (!params || Object.keys(params).length === 0) return <></>;
        return (
          <CellFlex>
            {Object.entries(params).map(([key, change]) => {
              const label = `${key}: ${formatParamValue(change?.new)}`;
              return (
                <ShrinkableChipWrapper key={key}>
                  <Tooltip title={label}>
                    <span>
                      <Chip label={label} customVariant="dashed" />
                    </span>
                  </Tooltip>
                </ShrinkableChipWrapper>
              );
            })}
          </CellFlex>
        );
      },
    }),
    [],
  );

  const buildGenericChangeColumn = useCallback(
    (
      id: string,
      header: string,
      getField: (row: TRow) => FieldChange<unknown> | undefined,
    ): Column<TRow> => ({
      id,
      header,
      showValueWhenEmpty: true,
      filterable: false,
      nonToggleable: true,
      width: 240,
      renderer: (row: TRow) => {
        const field = getField(row);
        if (!field) return <></>;
        const currentLabel = formatConfigChangeValue(field.current);
        const newLabel = formatConfigChangeValue(field.new);
        const isChanged = currentLabel !== newLabel;
        const showCurrent = isChanged && !row.isCreateRow;

        return (
          <CellFlex>
            {showCurrent && (
              <ShrinkableChipWrapper>
                <Tooltip title={currentLabel}>
                  <span>
                    <Chip label={currentLabel} customVariant="crossed" />
                  </span>
                </Tooltip>
              </ShrinkableChipWrapper>
            )}
            <ShrinkableChipWrapper>
              <Tooltip title={newLabel}>
                <span>
                  <Chip label={newLabel} customVariant="dashed" />
                </span>
              </Tooltip>
            </ShrinkableChipWrapper>
          </CellFlex>
        );
      },
    }),
    [],
  );

  return {
    buildPeriodColumn,
    buildNumberColumn,
    buildDropdownColumn,
    buildTextColumn,
    buildBooleanColumn,
    buildListColumn,
    buildParamsColumn,
    buildGenericChangeColumn,
  };
};

export default useConfigPreviewColumnBuilders;
